/**
 * Short-lived AWS credentials returned by your authProvider.
 *
 * The module assumes STS-vended credentials (sts:AssumeRole). The
 * `sessionToken` field is REQUIRED — it's sent as the `x-amz-security-token`
 * header in every S3 PutObject. Long-term IAM user credentials (no session
 * token) are not supported; S3 will reject the request with HTTP 400.
 */
export interface AwsCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
  expiration: Date;
}

export type AuthProvider = () => Promise<AwsCredentials>;

export interface TelemetryConfig {
  bucketName: string;
  region: string;
  appName: string;
  environment: 'production' | 'staging' | 'development' | 'test';
  authProvider: AuthProvider;
  batchSize?: number;
  flushIntervalMs?: number;
  samplingRate?: number;
  /**
   * Debug mode for local validation. When `true`:
   * - S3 uploads are skipped entirely (no fetch, no retry queue, no flush timer).
   * - Every logged event is appended to a localStorage array under the key
   *   `@entourage/ecs-telemetry-module:debug-events` (FIFO cap of 500 events).
   * - One `console.info` line is emitted at init noting that debug is active.
   *
   * Inspect events in DevTools → Application → Local Storage. Wire from your
   * consumer with an env var, e.g.:
   *   `debug: process.env.NEXT_PUBLIC_TELEMETRY_DEBUG === 'true'`
   */
  debug?: boolean;
}

export type EventType = 'viewed' | 'clicked' | 'started' | 'completed' | 'abandoned' | 'error';

export interface LoggedEvent {
  userId?: string;
  userEmail?: string;
  yearbookId?: string;
  brandId?: string;
  partnerId?: string;

  area: string | null;
  resource: string;
  action: string;

  eventType: EventType;
  success: boolean;

  correlationId?: string;

  metadata?: Record<string, unknown>;
}

export interface UserContext {
  userId: string;
  userEmail: string;
  brandId: string;
  partnerId: string;
}

export interface ProjectContext {
  yearbookId: string;
}

export interface EnrichedEvent
  extends Omit<LoggedEvent, 'userId' | 'userEmail' | 'yearbookId' | 'brandId' | 'partnerId'> {
  userId: string;
  userEmail: string;
  yearbookId: string;
  brandId: string;
  partnerId: string;
  eventId: string;
  sessionId: string;
  timestamp: string;
  appName: string;
  environment: string;
  schemaVersion: string;
}

export const SCHEMA_VERSION = '2.0.0';

export interface RetryBatch {
  batchId: string;
  events: EnrichedEvent[];
  retryCount: number;
  lastAttemptAt: number;
}

export interface DeadLetterBatch {
  batchId: string;
  events: EnrichedEvent[];
  droppedAt: number;
  reason: string;
}

export interface TelemetryDiagnostics {
  droppedBySampling: number;
  droppedByClientRateLimit: number;
  deadLettered: number;
  queueSize: number;
  retryQueueSize: number;
}
